import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import TimeSheetFilter from "./TimeSheetFilter";
import Typography from "../../Utility/Typography";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import CommonModal from "../../Utility/CommonModel/CommonModal";
import Button from "../../Utility/Button/Button";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { fetchUsersData } from "../../Redux/Thunks/usersThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { selectUsersData } from "../../Redux/Slices/usersSlice";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import LogTimeModal from "../Issues/LogTimeModal/LogTimeModal";
import { fetchProjectTimeSummary } from "../../Redux/Thunks/timeEntryThunk";
import { getListFromApiResponse } from "../utils/apiListMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
import { buildUserDropdownOptionsFromUsersData } from "../../utils/userFilterOptions";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import {
  formatDurationWithUnit,
  parseDurationToMinutes,
  resolveGrandTotalFromMetaOrRows,
} from "./timeEntryDisplayUtils";
import TimeEntryTableTotalsBar from "./TimeEntryTableTotalsBar";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import {
  LIST_PAGE_SIZE_OPTIONS,
  readStoredListPageSize,
  writeStoredListPageSize,
  resolveListTotalPages,
  TIME_ENTRY_PROJECT_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
} from "../../Utility/Pagination/listPaginationConstants";
import { getTodayDateRange } from "../../utils/dateUtils";
import {
  buildTimeSummaryIdQueryParams,
  buildProjectSummaryExportQueryParams,
} from "../../utils/timeEntryQueryParams";
import { downloadProjectSummaryExport } from "../../utils/downloadTimeLogsExport";
import { showSuccess, showError } from "../../utils/toast";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";

function ProjectTimeSummary({
  data = [],
  loading = false,
  error = null,
  dateRangeLabel,
  onPrevWeek,
  onNextWeek,
  disableNext,
  weekDateFrom,
  weekDateTo,
  filterExpanded,
  onFilterExpandedChange,
  onResetToToday,
}) {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const teamsData = useSelector(selectTeamsData);
  const usersData = useSelector(selectUsersData);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const teamsListLen = getListFromApiResponse(teamsData).length;
  const usersListLen = getListFromApiResponse(usersData).length;
  const clientsListLen = getListFromApiResponse(clientsData).length;
  const projectsListLen = getListFromApiResponse(projectsData).length;

  // Fetch teams and users data on mount
  useEffect(() => {
    if (teamsListLen === 0) dispatch(fetchTeamsData({ getAll: true }));
    if (usersListLen === 0) dispatch(fetchUsersData({ getAll: true }));
    if (clientsListLen === 0) dispatch(fetchClientsData({ getAll: true }));
    if (projectsListLen === 0) dispatch(fetchProjectsData({ getAll: true }));
  }, [dispatch, teamsListLen, usersListLen, clientsListLen, projectsListLen]);

  /* ===================== FILTER STATE (multiselect) ===================== */
  const [draftFilters, setDraftFilters] = useState({
    client: [], // multiselect values
    project: [], // multiselect values
    team: [], // multiselect values
    user: [], // multiselect values
  });
  const [appliedFilters, setAppliedFilters] = useState({
    client: [], // multiselect values
    project: [], // multiselect values
    team: [], // multiselect values
    user: [], // multiselect values
  });

  const [draftDateRange, setDraftDateRange] = useState(null); // { startDate, endDate } from DateRangePicker
  const [appliedDateRange, setAppliedDateRange] = useState(null); // applied range for API (dateFrom/dateTo)

  const defaultDateRangeForFilter = useMemo(() => {
    if (!weekDateFrom || !weekDateTo) return null;
    return { startDate: weekDateFrom, endDate: weekDateTo };
  }, [weekDateFrom, weekDateTo]);

  const rangeForApi = appliedDateRange ?? defaultDateRangeForFilter;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readStoredListPageSize(TIME_ENTRY_PROJECT_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY)
  );

  useEffect(() => {
    setPage(1);
  }, [weekDateFrom, weekDateTo]);

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}:${mins.toString().padStart(2, "0")} hr`;
  };

  const formatMinutesPlain = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}:${mins.toString().padStart(2, "0")}`;
  };

  /* ===================== NORMALIZE & GROUP DATA (DATE → PROJECT) ===================== */
  const groupedData = useMemo(() => {
    if (!data) return [];

    let groups = [];

    if (Array.isArray(data.groups)) {
      groups = data.groups;
    } else if (Array.isArray(data.list)) {
      // API sends flat list: group by full-date field in "day"
      const byDate = new Map();
      data.list.forEach((entry) => {
        const dateLabel = entry.day || entry.dateLabel || "Timesheet";
        if (!byDate.has(dateLabel)) {
          byDate.set(dateLabel, { dateLabel, entries: [] });
        }
        byDate.get(dateLabel).entries.push(entry);
      });
      groups = Array.from(byDate.values());
    }

    if (!Array.isArray(groups) || groups.length === 0) return [];

    return groups.map((group) => {
      // First aggregate by (project + task) for this date — server already applied filters
      const aggregateMap = new Map();

      (group.entries || []).forEach((entry) => {
        const memberName = entry.member || entry.assignee || entry.owner || "";

        const projectName = entry.project || "Unknown Project";
        const taskName = entry.task || "No Task";
        const key = `${projectName}__${taskName}`;

        const durationStr = entry.duration || entry.time || "";
        const minutes = parseDurationToMinutes(durationStr);

        if (!aggregateMap.has(key)) {
          aggregateMap.set(key, {
            id: entry.id,
            project: projectName,
            task: taskName,
            date: group.dateLabel,
            totalMinutes: 0,
            logs: [],
            entries: [], // Store all individual entries for the modal
          });
        }

        const agg = aggregateMap.get(key);
        agg.totalMinutes += minutes;
        if (entry.description) {
          agg.logs.push(entry.description);
        }
        // Store individual entry with member, description, duration
        // memberName is already declared above in the same loop iteration
        agg.entries.push({
          member: memberName || "Unknown",
          description: entry.description || "",
          duration: durationStr,
        });
      });

      // Now regroup by project so UI can show "group by project" under each date
      const projectMap = new Map();
      aggregateMap.forEach((agg) => {
        const projectName = agg.project;
        const row = {
          id: agg.id,
          project: agg.project,
          task: agg.task,
          date: agg.date,
          loggedDate: agg.date,
          time: formatMinutes(agg.totalMinutes),
          logs: agg.logs,
          entries: agg.entries, // Include all individual entries for the modal
        };

        if (!projectMap.has(projectName)) {
          projectMap.set(projectName, []);
        }
        projectMap.get(projectName).push(row);
      });

      return {
        dateLabel: group.dateLabel,
        projects: Array.from(projectMap.entries()).map(([project, entries]) => ({
          project,
          entries,
        })),
      };
    });
  }, [data]);

  const isProjectSummaryApiShape = useMemo(() => {
    return (
      Array.isArray(data?.list) &&
      data.list.some((entry) => Object.prototype.hasOwnProperty.call(entry, "logCount"))
    );
  }, [data]);

  // Flatten grouped data into simple row list for UI rendering
  const rows = useMemo(() => {
    if (isProjectSummaryApiShape) {
      return (data?.list || []).map((projectRow) => {
        const entries = Array.isArray(projectRow.entries) ? projectRow.entries : [];
        return {
          id: projectRow.id || projectRow.projectId,
          project: projectRow.project || "Unknown Project",
          task: projectRow.task ?? "—",
          loggedDate: projectRow.loggedDate ?? "—",
          time: projectRow.time || "0:00",
          logCount: Number(projectRow.logCount) || 0,
          entries,
        };
      });
    }

    return groupedData.flatMap((group) =>
      group.projects.flatMap((projectGroup) => projectGroup.entries)
    );
  }, [groupedData, isProjectSummaryApiShape, data]);

  const {
    sortedData: sortedRows,
    sortKey,
    sortDirection,
    handleSort,
  } = useSorting(rows, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  const grandTotal = useMemo(
    () =>
      resolveGrandTotalFromMetaOrRows(data?.meta, sortedRows, (row) =>
        row?.totalMinutes != null && Number.isFinite(Number(row.totalMinutes))
          ? Number(row.totalMinutes)
          : parseDurationToMinutes(row?.time)
      ),
    [data?.meta, sortedRows]
  );

  const totalPages = useMemo(
    () => resolveListTotalPages(data?.meta, pageSize, rows.length),
    [data?.meta, pageSize, rows.length]
  );

  /* ===================== FILTER OPTIONS ===================== */
  const filterOptions = useMemo(() => {
    const clientsList = getListFromApiResponse(clientsData);
    const teamsList = getListFromApiResponse(teamsData);

    const clients = clientsList
      .map((c) => c.clientName ?? c.name ?? "")
      .filter(Boolean)
      .sort();

    const teams = teamsList
      .map((t) => t.teamName ?? t.name ?? "")
      .filter(Boolean)
      .sort();

    const users = buildUserDropdownOptionsFromUsersData(usersData);

    return {
      clients,
      projects: mapProjectsToFilterOptions(projectsData),
      teams,
      users,
    };
  }, [clientsData, projectsData, teamsData, usersData]);

  /* ===================== HANDLERS ===================== */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const cleared = {
      client: [],
      project: [],
      team: [],
      user: [],
    };
    const todayRange = getTodayDateRange();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setDraftDateRange(todayRange);
    setAppliedDateRange(todayRange);
    setPage(1);
    onResetToToday?.();
  };

  const [isLogTimeOpen, setIsLogTimeOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);

  const tryOpenLogTime = () => {
    if (!can(PERMISSION_KEYS.TIME_ENTRY_CREATE)) {
      setDeniedPermissionKey(PERMISSION_KEYS.TIME_ENTRY_CREATE);
      return;
    }
    setIsLogTimeOpen(true);
  };

  const toYMD = (raw) => {
    if (!raw) return undefined;
    const s = String(raw);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  };

  // GET /time-logs/project-summary — comma-separated UUIDs for multiselect filters
  const projectSummaryQueryParams = useMemo(() => {
    const dateFrom = toYMD(rangeForApi?.startDate) || undefined;
    const dateTo = toYMD(rangeForApi?.endDate) || undefined;
    return {
      includeDetails: true,
      ...buildTimeSummaryIdQueryParams({
        appliedFilters,
        clientsData,
        teamsData,
        projectsData,
      }),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      limit: pageSize,
    };
  }, [
    appliedFilters,
    clientsData,
    teamsData,
    projectsData,
    rangeForApi?.startDate,
    rangeForApi?.endDate,
    page,
    pageSize,
  ]);

  useEffect(() => {
    dispatch(fetchProjectTimeSummary(projectSummaryQueryParams));
  }, [dispatch, projectSummaryQueryParams]);

  const refetchProjectSummaryAfterLog = useCallback(() => {
    dispatch(fetchProjectTimeSummary(projectSummaryQueryParams));
  }, [dispatch, projectSummaryQueryParams]);

  const applyFiltersHandler = () => {
    setAppliedFilters(draftFilters);
    setAppliedDateRange(draftDateRange ?? defaultDateRangeForFilter);
    setPage(1);
    onFilterExpandedChange?.(true);
  };

  const handleExportProjectSummaryCsv = useCallback(async () => {
    try {
      const params = buildProjectSummaryExportQueryParams(projectSummaryQueryParams);
      await downloadProjectSummaryExport(params, "csv");
      showSuccess("Project summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [projectSummaryQueryParams]);

  const handleExportProjectSummaryXlsx = useCallback(async () => {
    try {
      const params = buildProjectSummaryExportQueryParams(projectSummaryQueryParams);
      await downloadProjectSummaryExport(params, "xlsx");
      showSuccess("Project summary export downloaded.");
    } catch (e) {
      if (isSilentRequestAbortError(e)) return;
      showError(typeof e === "string" ? e : e?.message || "Export failed.");
    }
  }, [projectSummaryQueryParams]);

  /** Modal: group log entries by user (memberId or display name) */
  const logEntriesByUser = useMemo(() => {
    if (!selectedLog?.entries?.length) return [];

    const groupsMap = new Map();
    for (const entry of selectedLog.entries) {
      const key =
        entry.memberId != null && entry.memberId !== ""
          ? String(entry.memberId)
          : `name:${entry.member || "unknown"}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          member: entry.member || "Unknown",
          memberEmail: entry.memberEmail || "",
          entries: [],
          totalMinutes: 0,
        });
      }
      const g = groupsMap.get(key);
      g.entries.push(entry);
      g.totalMinutes += parseDurationToMinutes(entry.duration);
    }

    return Array.from(groupsMap.entries()).map(([groupKey, value]) => ({
      groupKey,
      ...value,
    }));
  }, [selectedLog]);

  /* ===================== UI ===================== */
  return (
    <div className="w-full">
      <TimeSheetFilter
        filters={draftFilters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        showTask={false} // task dropdown not showing
        showTeam={true} // show team dropdown
        showUser={true} // show user dropdown
        showStatus={false} // hide status dropdown
        showBillingType={false} // hide billing type dropdown
        onLogTime={tryOpenLogTime}
        onSync={() => dispatch(fetchProjectTimeSummary(projectSummaryQueryParams))}
        onExportCsv={handleExportProjectSummaryCsv}
        onExportXlsx={handleExportProjectSummaryXlsx}
        dateRangeLabel={dateRangeLabel}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        disableNext={disableNext}
        dateRangeDraft={draftDateRange ?? defaultDateRangeForFilter}
        onDateRangeDraftChange={setDraftDateRange}
        handleFilterReset={handleFilterReset}
        applyFilters={applyFiltersHandler}
        filterExpanded={filterExpanded}
        onFilterExpandedChange={onFilterExpandedChange}
      />

      {isLogTimeOpen && (
        <LogTimeModal
          isOpen={isLogTimeOpen}
          onClose={() => setIsLogTimeOpen(false)}
          issue={null}
          onSuccess={refetchProjectSummaryAfterLog}
        />
      )}
      <ContentLoader loading={loading} className="min-h-[40vh]">
        {!loading && error ? (
          <NoDataFound message={error || "Failed to load project time summary"} />
        ) : !loading && !rows.length ? (
          <NoDataFound message="No project time summary data available" />
        ) : !loading ? (
          <div className="mt-4">
            <TimeEntryTableTotalsBar
              dateRangeLabel={dateRangeLabel}
              totalLabel={grandTotal.totalLabel}
            />
            <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
              <div className="flex-1 p-2">
                <div className="live-preview">
                  <div className="overflow-x-auto">
                    <table className="w-full mb-0 clients-table">
                      <thead className="bg-surface-secondary">
                        <tr>
                          <SortableHeader
                            columnKey="project"
                            label="Project"
                            onSort={handleSort}
                            currentSortKey={sortKey}
                            sortDirection={sortDirection}
                            sortable
                            className="px-4 py-2 align-middle"
                          />
                          <SortableHeader
                            columnKey="task"
                            label="Task"
                            onSort={handleSort}
                            currentSortKey={sortKey}
                            sortDirection={sortDirection}
                            sortable
                            className="px-4 py-2 align-middle"
                          />
                          <SortableHeader
                            columnKey="loggedDate"
                            label="Logged date"
                            onSort={handleSort}
                            currentSortKey={sortKey}
                            sortDirection={sortDirection}
                            sortable
                            className="px-4 py-2 align-middle"
                          />
                          <SortableHeader
                            columnKey="time"
                            label="Time"
                            onSort={handleSort}
                            currentSortKey={sortKey}
                            sortDirection={sortDirection}
                            sortable
                            className="px-4 py-2 align-middle"
                          />
                          <SortableHeader
                            columnKey="log"
                            label="Log"
                            onSort={handleSort}
                            currentSortKey={sortKey}
                            sortDirection={sortDirection}
                            sortable={false}
                            className="px-4 py-2 align-middle action-col"
                          />
                        </tr>
                      </thead>
                      <tbody className="relative">
                        {sortedRows.map((entry) => (
                          <tr key={entry.id} className="border-t border-border-primary">
                            <td className="text-start align-middle px-4 py-2">
                              <Typography className="font-medium">{entry.project}</Typography>
                            </td>
                            <td className="text-start align-middle px-4 py-2">
                              <Typography>{entry.task}</Typography>
                            </td>
                            <td className="text-start align-middle px-4 py-2">
                              <Typography>{entry.loggedDate ?? entry.date}</Typography>
                            </td>
                            <td className="text-start align-middle px-4 py-2">
                              <Typography>{formatDurationWithUnit(entry.time)}</Typography>
                            </td>
                            <td className="text-start align-middle action-col px-4 py-2 !whitespace-nowrap min-w-[4.75rem]">
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="whitespace-nowrap shrink-0"
                                onClick={() => {
                                  setSelectedLog(entry);
                                  setIsLogModalOpen(true);
                                }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {rows.length > 0 ? (
              <ListPaginationBar
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  writeStoredListPageSize(
                    TIME_ENTRY_PROJECT_SUMMARY_LIST_PAGE_SIZE_STORAGE_KEY,
                    next
                  );
                  setPageSize(next);
                  setPage(1);
                }}
                pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                className="px-2 pb-2"
              />
            ) : null}
          </div>
        ) : null}
      </ContentLoader>

      <CommonModal
        isOpen={isLogModalOpen && !!selectedLog}
        onClose={() => {
          setIsLogModalOpen(false);
          setSelectedLog(null);
        }}
        title="Log Details"
        width="max-w-4xl"
      >
        {selectedLog && (
          <div className="space-y-4">
            {/* Summary Info */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <Typography variant="span2" className="font-semibold" component="span">
                  Project:
                </Typography>{" "}
                {selectedLog.project}
              </div>
              <div>
                <Typography variant="span2" className="font-semibold" component="span">
                  Task:
                </Typography>{" "}
                {selectedLog.task || "-"}
              </div>
              <div>
                <Typography variant="span2" className="font-semibold" component="span">
                  Logged date:
                </Typography>{" "}
                {selectedLog.loggedDate || selectedLog.date || "-"}
              </div>
              <div>
                <Typography variant="span2" className="font-semibold" component="span">
                  Total Time:
                </Typography>{" "}
                {formatDurationWithUnit(selectedLog.time)}
              </div>
            </div>

            {/* Entries grouped by user */}
            <div className="mt-4">
              <Typography className="text-sm font-semibold mb-2">Log entries by user</Typography>
              {logEntriesByUser.length > 0 ? (
                <div className="space-y-6">
                  {logEntriesByUser.map((userGroup) => (
                    <div
                      key={userGroup.groupKey}
                      className="rounded-md border border-border-primary overflow-hidden"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 bg-surface-secondary border-b border-border-primary">
                        <div>
                          <Typography className="text-sm font-semibold">
                            {userGroup.member}
                          </Typography>
                          {userGroup.memberEmail ? (
                            <Typography variant="span2" className="text-text-secondary text-xs">
                              {userGroup.memberEmail}
                            </Typography>
                          ) : null}
                        </div>
                        <Typography className="text-sm text-text-secondary">
                          Subtotal: {formatDurationWithUnit(userGroup.totalMinutes)}
                        </Typography>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-surface-primary">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold border-b border-border-primary">
                                Task
                              </th>
                              <th className="px-3 py-2 text-left font-semibold border-b border-border-primary">
                                Logged date
                              </th>
                              <th className="px-3 py-2 text-left font-semibold border-b border-border-primary">
                                Description
                              </th>
                              <th className="px-3 py-2 text-left font-semibold border-b border-border-primary">
                                Duration
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {userGroup.entries.map((entry, idx) => (
                              <tr
                                key={entry.id ?? `${userGroup.member}-${idx}`}
                                className="border-b border-border-primary last:border-b-0"
                              >
                                <td className="px-3 py-2">{entry.task || "-"}</td>
                                <td className="px-3 py-2">
                                  {entry.loggedDate || entry.date || "-"}
                                </td>
                                <td className="px-3 py-2">{entry.description || "-"}</td>
                                <td className="px-3 py-2">
                                  {formatDurationWithUnit(entry.duration)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography className="text-sm text-text-secondary">
                  No log entries available.
                </Typography>
              )}
            </div>
          </div>
        )}
      </CommonModal>

      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
}

export default ProjectTimeSummary;
